<script lang="ts" setup>
import GridView from "./GridView.vue";
import ListView from "./ListView.vue";
import { reactive, computed, onBeforeUnmount, PropType, watch } from "vue";
import {
  DocCopyDialog,
  LoadingSpinner,
  ShareDocumentModal,
} from "@/components";
import { useRouter, useRoute } from "vue-router";
import {
  useWorkspaceDocumentsQuery,
  WorkspaceDocumentFragment,
  ChildWorkspaceFragment,
} from "@/graphql/operations";
import type { WorkspaceMove } from "@/composables/useWorkspaceOrdering";
import { useStore } from "vuex";

const router = useRouter();
const route = useRoute();
const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);

const props = defineProps({
  layout: {
    type: String,
    required: true,
  },
  subfolders: {
    type: Object as PropType<Array<ChildWorkspaceFragment>>,
    required: true,
  },
  subfolderOrdering: {
    type: Boolean,
    default: false,
  },
  subfolderOrderingDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  edit: [id: string];
  share: [id: string];
  move: [id: string];
  moveSubfolder: [move: WorkspaceMove];
  subfolderOrderingGesture: [active: boolean];
}>();

const workspaceId = route.params.workspaceId as string;

enum CopyAction {
  MOVE = "MOVE",
  COPY = "COPY",
}

interface ComponentData {
  editorActive: boolean;
  documentEditId: string | null;
  openDocId: string | null;
  copyDialogActive: boolean;
  copyDoc: WorkspaceDocumentFragment | null;
  copyAction: CopyAction | null;
  documentSpacePickerActive: boolean;
  spacePickerDocId: string | null;
}

const state: ComponentData = reactive({
  editorActive: false,
  documentEditId: null,
  openDocId: null,
  copyDialogActive: false,
  copyDoc: null,
  copyAction: null,
  documentSpacePickerActive: false,
  spacePickerDocId: null,
});

const { data, fetching, executeQuery } = useWorkspaceDocumentsQuery({
  variables: {
    workspaceId: workspaceId,
  },
  requestPolicy: "cache-and-network",
});

const intervalId = setInterval(() => {
  executeQuery();
}, 4 * 1000);

onBeforeUnmount(() => {
  clearInterval(intervalId);
});

const documents = computed(() => data.value?.documents?.nodes);

function navigateToEditor(docId: string) {
  router.push({
    name: "DocumentProperties",
    params: {
      workspaceId: route.params.workspaceId,
      documentId: docId,
    },
  });
}

function showMoveDialog(docId: string) {
  const doc = documents.value?.find((d) => d!.id === docId);
  if (!doc) {
    return;
  }

  state.copyDoc = doc;
  state.copyAction = CopyAction.MOVE;
  state.copyDialogActive = true;
}

function closeCopyDialog() {
  state.copyDialogActive = false;
  state.copyAction = null;
  state.copyDoc = null;
}

function showDocumentSpacePicker(pvDocId: string) {
  if (!canUseSharedSpaces.value) return;
  state.spacePickerDocId = pvDocId;
  state.documentSpacePickerActive = true;
}

watch(canUseSharedSpaces, (enabled) => {
  if (!enabled) state.documentSpacePickerActive = false;
});
</script>

<template>
  <div v-if="fetching && !data" class="loading_wrapper">
    <div class="loading_status">
      <LoadingSpinner :color="'var(--classic-text, black)'" />
    </div>
  </div>

  <div
    v-else-if="
      subfolders.length === 0 && (!documents || documents.length === 0)
    "
    class="info"
  >
    <div>
      <div>
        <span class="material-symbols-outlined"> folder_open </span>
      </div>
      <span class="text">{{ $t("pvdocuments.empty") }}</span>
    </div>
  </div>

  <template v-else>
    <ListView
      v-if="layout === 'list_view'"
      :subfolders="subfolders"
      :documents="documents as WorkspaceDocumentFragment[]"
      :subfolder-ordering="subfolderOrdering"
      :subfolder-ordering-disabled="subfolderOrderingDisabled"
      @edit="navigateToEditor"
      @share="showDocumentSpacePicker"
      @move="showMoveDialog"
      @move-subfolder="emit('moveSubfolder', $event)"
      @subfolder-ordering-gesture="emit('subfolderOrderingGesture', $event)"
    />

    <GridView
      v-else
      :subfolders="subfolders"
      :documents="documents as WorkspaceDocumentFragment[]"
      :subfolder-ordering="subfolderOrdering"
      :subfolder-ordering-disabled="subfolderOrderingDisabled"
      @share="showDocumentSpacePicker"
      @move="showMoveDialog"
      @move-subfolder="emit('moveSubfolder', $event)"
      @subfolder-ordering-gesture="emit('subfolderOrderingGesture', $event)"
    />
  </template>

  <doc-copy-dialog
    v-if="state.copyDialogActive"
    :doc="state.copyDoc!"
    :action="state.copyAction!"
    @close="closeCopyDialog"
  />

  <ShareDocumentModal
    v-if="canUseSharedSpaces && state.documentSpacePickerActive"
    :docId="state.spacePickerDocId!"
    :inherit-classic-theme="true"
    @close="state.documentSpacePickerActive = false"
  />
</template>
